import React from 'react';
import { Activity, Calculator, Heart } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для быстрой настройки параметров
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating
}) => {
  // Обработчик изменения возраста
  const handleAgeChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      age: newValue
    }));
  };

  // Обработчик изменения пола
  const handleGenderChange = (gender) => {
    setFormData(prev => {
      const newState = { ...prev, gender };
      
      // Переключаем на формулу Гулати для женщин
      if (gender === 'female' && (prev.formula === 'tanaka' || prev.formula === 'haskell')) {
        newState.formula = 'gulati';
      } else if (gender === 'male' && prev.formula === 'gulati') {
        // Для мужчин используем формулу Танаки
        newState.formula = 'tanaka';
      }
      
      return newState;
    });
  };
  
  // Обработчик изменения пульса в покое
  const handleRestingHeartRateChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      restingHeartRate: newValue
    }));
  };
  
  // Обработчик изменения формулы
  const handleFormulaChange = (formula) => {
    setFormData(prev => ({
      ...prev,
      formula
    }));
  };

  // Функция расчета
  const calculateHeartRate = () => {
    // Вызываем функцию основного расчета
    document.querySelector('button[type="submit"]')?.click();
  };
  
  // Предопределенные значения возраста для быстрого выбора
  const predefinedAges = [
    { value: '20', label: '20 лет' },
    { value: '30', label: '30 лет' },
    { value: '40', label: '40 лет' },
    { value: '50', label: '50 лет' }
  ];
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Calculator className="w-5 h-5 mr-2 text-red-500" />
        Быстрый расчет
      </h3>
      
      <div className="space-y-5">
        {/* Возраст */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="ageSlider" className="text-gray-600 dark:text-gray-400">Возраст</label>
            <span className="font-medium text-gray-900 dark:text-white">{formData.age} лет</span>
          </div>
          <input
            type="range"
            id="ageSlider"
            min="1"
            max="100"
            step="1"
            value={formData.age}
            onChange={handleAgeChange}
            aria-label="Возраст"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-red-600"
          />
          
          {/* Кнопки с предопределенными возрастами */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {predefinedAges.map(age => (
              <button
                key={age.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, age: age.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.age === age.value 
                    ? 'bg-red-100 border-red-300 text-red-700 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {age.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Пол */}
        <div>
          <div className="flex justify-between items-center mb-2 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Пол</label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => handleGenderChange('male')}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.gender === 'male' 
                  ? 'bg-red-100 border-red-300 text-red-700 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Мужской</div>
              <div className="text-xs opacity-75">Танака: 208 - 0.7×возраст</div>
            </button>
            <button
              type="button"
              onClick={() => handleGenderChange('female')}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.gender === 'female' 
                  ? 'bg-red-100 border-red-300 text-red-700 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Женский</div>
              <div className="text-xs opacity-75">Гулати: 206 - 0.88×возраст</div>
            </button>
          </div>
        </div>
        
        {/* Пульс в покое */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="restingHeartRateSlider" className="text-gray-600 dark:text-gray-400">Пульс в покое</label>
            <span className="font-medium text-gray-900 dark:text-white">
              {formData.restingHeartRate} уд/мин
            </span>
          </div>
          <input
            type="range"
            id="restingHeartRateSlider"
            min="40"
            max="100"
            step="1"
            value={formData.restingHeartRate}
            onChange={handleRestingHeartRateChange}
            aria-label="Пульс в покое (уд/мин)"
            className={`w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-red-600 ${!formData.includeRestingHeartRate ? 'opacity-50' : ''}`}
            disabled={!formData.includeRestingHeartRate}
          />
          <div className="mt-1 flex items-center">
            <input
              type="checkbox"
              id="includeRestingHeartRateCheckbox"
              name="includeRestingHeartRate"
              checked={formData.includeRestingHeartRate}
              onChange={(e) => setFormData(prev => ({ ...prev, includeRestingHeartRate: e.target.checked }))}
              className="form-checkbox mr-2"
            />
            <label htmlFor="includeRestingHeartRateCheckbox" className="text-xs text-gray-600 dark:text-gray-400 cursor-pointer">
              Учитывать пульс в покое (Карвонен)
            </label>
          </div>
        </div>
        
        {/* Формула расчета */}
        <div>
          <div className="text-sm text-gray-600 dark:text-gray-400 mb-2">Формула расчета</div>
          <div className="grid grid-cols-1 gap-1">
            <button
              type="button"
              onClick={() => handleFormulaChange('tanaka')}
              className={`py-1.5 px-2 text-xs rounded border transition-colors
                ${formData.formula === 'tanaka' 
                  ? 'bg-red-100 border-red-300 text-red-700 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
              disabled={formData.gender === 'female'}
            >
              Танака (рекомендуется): 208 - 0.7 × возраст
            </button>
            <button
              type="button"
              onClick={() => handleFormulaChange('haskell')}
              className={`py-1.5 px-2 text-xs rounded border transition-colors
                ${formData.formula === 'haskell' 
                  ? 'bg-red-100 border-red-300 text-red-700 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
              disabled={formData.gender === 'female'}
            >
              Хаскелл-Фокс (классическая): 220 - возраст
            </button>
            <button
              type="button"
              onClick={() => handleFormulaChange('gulati')}
              className={`py-1.5 px-2 text-xs rounded border transition-colors
                ${formData.formula === 'gulati' 
                  ? 'bg-red-100 border-red-300 text-red-700 dark:bg-red-900/30 dark:border-red-700 dark:text-red-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
              disabled={formData.gender === 'male'}
            >
              Гулати (для женщин): 206 - 0.88 × возраст
            </button>
          </div>
        </div>
        
        {/* Кнопка расчета */}
        <Button
          primary
          fullWidth
          onClick={calculateHeartRate}
          icon={<Heart className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать пульс
        </Button>
      </div>
      
      {/* Информация о максимальном пульсе */}
      <div className="mt-5 pt-4 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-start">
          <Heart className="w-5 h-5 text-red-500 mr-2 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-gray-600 dark:text-gray-400">
            Максимальный пульс (MHR) — наивысшая частота сердечных сокращений, которую может достичь ваше сердце. Это важный показатель для определения оптимальных зон интенсивности тренировок.
          </p>
        </div>
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;